import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

// Frontend version baked in at build time
const FRONTEND_VERSION = import.meta.env.VITE_APP_VERSION;

// Enable verbose video debugging overlay (for TouchDesigner/CEF troubleshooting)
const VIDEO_DEBUG_ENABLED = false;

interface Post {
  _id: string;
  contentType: 'photo' | 'video' | 'voiceNote';
  caption?: string;
  originalFilename?: string;
  processedFilename?: string;
  user: {
    name: string;
  };
  createdAt: string;
}

interface MediaDimensions {
  width: number;
  height: number;
}

export default function DisplayPage() {
  const [post, setPost] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mediaDimensions, setMediaDimensions] = useState<MediaDimensions | null>(null);
  const [videoStatus, setVideoStatus] = useState<string>('loading');
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const hasConnectedOnce = useRef(false);

  const hasMediaDimensions = mediaDimensions !== null;
  const mediaAspectRatio = hasMediaDimensions
    ? mediaDimensions.width / mediaDimensions.height
    : null;
  const mediaFrameStyle = mediaAspectRatio
    ? {
        width: `min(90vw, calc(90vh * ${mediaAspectRatio}))`,
        height: `min(90vh, calc(90vw / ${mediaAspectRatio}))`,
      }
    : undefined;

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setMediaDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const handleVideoLoadedMetadata = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    setMediaDimensions({ width: video.videoWidth, height: video.videoHeight });
  };

  const fetchLatest = useCallback(async () => {
    try {
      const response = await fetch('/api/posts/latest');
      const data = await response.json();
      if (data.success && data.post) {
        setPost(data.post);
      }
    } catch (error) {
      console.error('Failed to fetch latest post:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkVersionAndReload = useCallback(async () => {
    // Skip version check in development mode
    if (FRONTEND_VERSION === 'dev') {
      console.log('Version check skipped in dev mode');
      return;
    }

    try {
      const response = await fetch('/api/version');
      const data = await response.json();
      const backendVersion = data.version;

      console.log(`Version check - Frontend: ${FRONTEND_VERSION}, Backend: ${backendVersion}`);

      if (backendVersion !== 'dev' && backendVersion !== FRONTEND_VERSION) {
        console.log('Version mismatch detected, reloading page...');
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to check version:', error);
    }
  }, []);

  useEffect(() => {
    fetchLatest();

    // Connect to Socket.io
    const socket: Socket = io({
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('Connected to server');

      // Check version on reconnect (not on initial connect)
      if (hasConnectedOnce.current) {
        console.log('Reconnected - checking version...');
        checkVersionAndReload();
        // Also fetch latest post in case we missed updates
        fetchLatest();
      }
      hasConnectedOnce.current = true;
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from server');
    });

    socket.on('new-post', (newPost: Post) => {
      setPost(newPost);
    });

    socket.on('post-updated', (updatedPost: Post) => {
      setPost((current) =>
        current?._id === updatedPost._id ? updatedPost : current
      );
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchLatest, checkVersionAndReload]);

  // Helper to add debug log (only when VIDEO_DEBUG_ENABLED)
  const addDebug = useCallback((msg: string) => {
    if (!VIDEO_DEBUG_ENABLED) return;
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[DEBUG] ${msg}`);
    setDebugInfo(prev => [`${timestamp}: ${msg}`, ...prev.slice(0, 19)]);
  }, []);

  // Reset media dimensions and video status when post changes
  useEffect(() => {
    setMediaDimensions(null);
    setVideoStatus('loading');
    setDebugInfo([]);
  }, [post?._id]);

  // Programmatic video playback for embedded browsers (e.g., TouchDesigner WebRender)
  useEffect(() => {
    if (post?.contentType !== 'video' || !videoRef.current) return;

    const video = videoRef.current;
    const url = `/uploads/${post.processedFilename || post.originalFilename}`;
    let playAttempts = 0;
    const maxAttempts = 5;
    let isPlaying = false;

    addDebug(`Video URL: ${url}`);
    addDebug(`Processed: ${post.processedFilename || 'none'}`);
    addDebug(`Original: ${post.originalFilename || 'none'}`);

    // First, check if video is accessible and get headers
    fetch(url, { method: 'HEAD' })
      .then(response => {
        addDebug(`Fetch status: ${response.status}`);
        addDebug(`Content-Type: ${response.headers.get('content-type')}`);
        addDebug(`Content-Length: ${response.headers.get('content-length')}`);
        addDebug(`Accept-Ranges: ${response.headers.get('accept-ranges')}`);
      })
      .catch(err => {
        addDebug(`Fetch error: ${err.message}`);
      });

    const logVideoState = (prefix: string) => {
      addDebug(`${prefix} readyState=${video.readyState} networkState=${video.networkState}`);
      addDebug(`${prefix} duration=${video.duration} currentTime=${video.currentTime}`);
      addDebug(`${prefix} videoWidth=${video.videoWidth} videoHeight=${video.videoHeight}`);
      if (video.error) {
        addDebug(`${prefix} error.code=${video.error.code} error.message=${video.error.message}`);
      }
    };

    const attemptPlay = async () => {
      if (isPlaying || playAttempts >= maxAttempts) return;
      playAttempts++;
      addDebug(`Play attempt ${playAttempts}/${maxAttempts}`);
      logVideoState(`Before play #${playAttempts}`);

      try {
        // Force load if needed
        if (video.readyState < 2) {
          addDebug(`Calling video.load() (readyState was ${video.readyState})`);
          video.load();
        }
        await video.play();
        isPlaying = true;
        addDebug('Video playing successfully!');
      } catch (error) {
        const err = error as Error;
        addDebug(`Play failed: ${err.name}: ${err.message}`);
        // Retry after delay
        setTimeout(attemptPlay, 500);
      }
    };

    // Event handlers with logging
    const onLoadStart = () => { addDebug('Event: loadstart'); logVideoState('loadstart'); };
    const onLoadedMetadata = () => { addDebug('Event: loadedmetadata'); logVideoState('loadedmetadata'); };
    const onLoadedData = () => { addDebug('Event: loadeddata'); logVideoState('loadeddata'); attemptPlay(); };
    const onCanPlay = () => { addDebug('Event: canplay'); logVideoState('canplay'); attemptPlay(); };
    const onCanPlayThrough = () => { addDebug('Event: canplaythrough'); attemptPlay(); };
    const onPlaying = () => { addDebug('Event: playing'); isPlaying = true; };
    const onWaiting = () => { addDebug('Event: waiting'); };
    const onStalled = () => { addDebug('Event: stalled'); logVideoState('stalled'); };
    const onSuspend = () => { addDebug('Event: suspend'); };
    const onAbort = () => { addDebug('Event: abort'); };
    const onEmptied = () => { addDebug('Event: emptied'); };
    const onError = () => {
      const err = video.error;
      const errorMessages: Record<number, string> = {
        1: 'MEDIA_ERR_ABORTED - fetching aborted by user',
        2: 'MEDIA_ERR_NETWORK - network error',
        3: 'MEDIA_ERR_DECODE - decoding error',
        4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - format not supported',
      };
      addDebug(`Event: error code=${err?.code} (${errorMessages[err?.code || 0] || 'unknown'})`);
      addDebug(`Error message: ${err?.message || 'none'}`);
      logVideoState('error');
    };

    video.addEventListener('loadstart', onLoadStart);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('canplaythrough', onCanPlayThrough);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('stalled', onStalled);
    video.addEventListener('suspend', onSuspend);
    video.addEventListener('abort', onAbort);
    video.addEventListener('emptied', onEmptied);
    video.addEventListener('error', onError);

    // Also try immediately and after short delays
    addDebug('Starting initial play attempts...');
    attemptPlay();
    setTimeout(attemptPlay, 100);
    setTimeout(attemptPlay, 500);

    return () => {
      video.removeEventListener('loadstart', onLoadStart);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('canplaythrough', onCanPlayThrough);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('stalled', onStalled);
      video.removeEventListener('suspend', onSuspend);
      video.removeEventListener('abort', onAbort);
      video.removeEventListener('emptied', onEmptied);
      video.removeEventListener('error', onError);
    };
  }, [post?._id, post?.contentType, post?.processedFilename, post?.originalFilename, addDebug]);

  // Sync background video with main video to prevent resource contention
  useEffect(() => {
    if (post?.contentType !== 'video' || !videoRef.current || !bgVideoRef.current) return;

    const main = videoRef.current;
    const bg = bgVideoRef.current;

    const syncVideos = () => {
      if (Math.abs(main.currentTime - bg.currentTime) > 0.1) {
        bg.currentTime = main.currentTime;
      }
    };

    main.addEventListener('timeupdate', syncVideos);
    return () => main.removeEventListener('timeupdate', syncVideos);
  }, [post?._id, post?.contentType]);

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    return `/uploads/${filename}`;
  };

  // Use processed file if available, otherwise original
  const mediaUrl = getMediaUrl(post?.processedFilename || post?.originalFilename);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white/50 animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-rose-500/20 to-fuchsia-600/20 flex items-center justify-center">
            <span className="text-5xl opacity-50">📡</span>
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-white/30">
              Ping Display
            </h1>
            <p className="text-white/20 text-sm mt-1">
              Waiting for content...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center overflow-hidden">
      {/* Video debug overlay (for TouchDesigner/CEF debugging) */}
      {VIDEO_DEBUG_ENABLED && post?.contentType === 'video' && (
        <div className="fixed top-4 left-4 z-50 bg-black/80 p-4 rounded-lg max-w-md max-h-[80vh] overflow-y-auto">
          <div className="text-yellow-400 text-sm font-mono font-bold mb-2">
            Video Debug - Status: {videoStatus}
          </div>
          <div className="space-y-1">
            {debugInfo.map((line, i) => (
              <div key={i} className="text-white/70 text-xs font-mono whitespace-pre-wrap">
                {line}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo content */}
      {post.contentType === 'photo' && mediaUrl && (
        <div className="relative w-full h-screen">
          {/* Blurred background */}
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={mediaUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover blur-3xl scale-110"
              style={{
                transformOrigin: 'center',
              }}
            />
          </div>

          {/* Main content in overlay box */}
          <div className="relative z-10 w-full h-full flex items-center justify-center p-8">
            <div
              className="relative rounded-lg overflow-hidden"
              style={{
                ...mediaFrameStyle,
                boxShadow: '0 25px 80px -10px rgba(0, 0, 0, 0.7), 0 10px 30px -5px rgba(0, 0, 0, 0.5)',
              }}
            >
              <img
                src={mediaUrl}
                alt=""
                className={
                  hasMediaDimensions
                    ? 'block w-full h-full object-contain'
                    : 'block max-w-[90vw] max-h-[90vh] object-contain'
                }
                onLoad={handleImageLoad}
              />
              {post.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-white text-6xl">{post.caption}</p>
                  <p className="text-white/50 text-4xl mt-4">
                    from {post.user.name.split(' ')[0]}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video content */}
      {post.contentType === 'video' && mediaUrl && (
        <div className="relative w-full h-screen">
          {/* Blurred background video */}
          <div className="absolute inset-0 overflow-hidden">
            <video
              key={`bg-${post._id}`}
              ref={bgVideoRef}
              src={mediaUrl}
              preload="auto"
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover blur-3xl scale-110"
              style={{
                transformOrigin: 'center',
              }}
            />
          </div>

          {/* Main content in overlay box */}
          <div className="relative z-10 w-full h-full flex items-center justify-center p-8">
            <div
              className="relative rounded-lg overflow-hidden"
              style={{
                ...mediaFrameStyle,
                boxShadow: '0 25px 80px -10px rgba(0, 0, 0, 0.7), 0 10px 30px -5px rgba(0, 0, 0, 0.5)',
              }}
            >
              <video
                key={post._id}
                ref={videoRef}
                src={mediaUrl}
                preload="auto"
                autoPlay
                loop
                muted
                playsInline
                className={
                  hasMediaDimensions
                    ? 'block w-full h-full object-contain'
                    : 'block max-w-[90vw] max-h-[90vh] object-contain'
                }
                onLoadedMetadata={handleVideoLoadedMetadata}
                onError={(e) => {
                  const code = e.currentTarget.error?.code;
                  setVideoStatus(`error: ${code}`);
                }}
                onLoadStart={() => setVideoStatus('loadstart')}
                onCanPlay={() => setVideoStatus('canplay')}
                onPlaying={() => setVideoStatus('playing')}
                onStalled={() => setVideoStatus('stalled')}
                onWaiting={() => setVideoStatus('waiting')}
              />
              {post.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-white text-6xl">{post.caption}</p>
                  <p className="text-white/50 text-4xl mt-4">
                    from {post.user.name.split(' ')[0]}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Voice note only - show simple indicator */}
      {post.contentType === 'voiceNote' && (
        <div className="text-center space-y-6">
          <div className="w-32 h-32 mx-auto rounded-full bg-primary/20 flex items-center justify-center animate-pulse-slow">
            <span className="text-6xl">🎙️</span>
          </div>
          <div>
            <p className="text-white/50 text-lg">Voice message</p>
            <p className="text-white/30 text-sm mt-1">
              from {post.user.name.split(' ')[0]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
